/**
 * Sticky Buy Bar Component.
 * Creates a sticky buy bar that appears at the bottom of the screen on mobile devices.
 * Provides quick access to the main product buy buttons with price updates on variant changes.
 */

import { PUB_SUB_EVENTS } from '../base/constants';
import { subscribe } from '../base/pubsub';

class StickyBuyBar extends HTMLElement {
  constructor() {
    super();
    this.scrollThreshold = 300;
    this.scrollDirection = 'down';
    this.lastScrollY = window.scrollY;
    this.isVisible = false;
    this.mainButton = null;
    this.variantChangeSubscription = null;
    this.scrollHandler = this.handleScroll.bind(this);
    this.resizeHandler = this.handleResize.bind(this);
    this.cleanup = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup?.();
  }

  /**
   * Initialises the sticky buy bar component.
   */
  init() {
    this.mainButton = this.querySelector('.js-sticky-buy-bar-main-button');

    if (!this.mainButton) return;

    this.setupEventListeners();
    this.subscribeToVariantChanges();
    this.updateButtonState();
  }

  /**
   * Sets up event listeners for scroll, resize, and button clicks.
   */
  setupEventListeners() {
    // Throttled scroll handler.
    let ticking = false;
    const throttledScrollHandler = () => {
      if (ticking) return;

      ticking = true;
      requestAnimationFrame(() => {
        this.scrollHandler();
        ticking = false;
      });
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    // Button click handler for main button.
    if (this.mainButton) {
      this.mainButton.addEventListener('click', this.handleButtonClick.bind(this));
    }

    // Store cleanup function.
    this.cleanup = () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      window.removeEventListener('resize', this.resizeHandler);
      if (this.variantChangeSubscription) {
        this.variantChangeSubscription();
      }
    };
  }

  /**
   * Handles scroll events to show/hide the sticky bar.
   */
  handleScroll() {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;

    // Determine scroll direction.
    this.scrollDirection = scrollY > this.lastScrollY ? 'down' : 'up';
    this.lastScrollY = scrollY;

    // Check if we should show/hide the sticky bar.
    const shouldShow = this.shouldShowStickyBar(scrollY, windowHeight);

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }

  /**
   * Handles resize events to re-evaluate sticky bar visibility.
   */
  handleResize() {
    this.handleScroll();
  }

  /**
   * Determines if the sticky bar should be shown based on scroll position.
   * @param {number} scrollY - Current scroll position.
   * @param {number} windowHeight - Window height.
   * @returns {boolean} - Whether the sticky bar should be shown.
   */
  shouldShowStickyBar(scrollY, windowHeight) {
    // Check if the main product buy button is in view.
    // First check for the original buy button, then check for personalisation button.
    const mainProductButton = document.querySelector('.js-main-product-button');
    const personalisationButton = document.querySelector('#react-personalisation-ui button');
    const mainButton = mainProductButton || personalisationButton;
    let isMainButtonInView = false;

    if (mainButton) {
      const buttonRect = mainButton.getBoundingClientRect();
      // Consider the button "in view" if it's visible in the viewport with some margin.
      isMainButtonInView = buttonRect.top < windowHeight && buttonRect.bottom > 0;
    }

    // Don't show if the main buy button is in view.
    if (isMainButtonInView) {
      return false;
    }

    // Check if we're near the footer.
    const footer = document.querySelector('footer');
    let isNearFooter = false;

    if (footer) {
      const footerRect = footer.getBoundingClientRect();
      // Approximate height of sticky bar in pixels.
      const stickyBarHeight = 80;
      isNearFooter = footerRect.top <= windowHeight - stickyBarHeight;
    }

    // Don't show if we're near the footer.
    if (isNearFooter) {
      return false;
    }

    // Check if show on scroll up is enabled.
    const showOnScrollUp = this.hasAttribute('data-show-on-scroll-up');

    // Show when scrolling down and past threshold, or when scrolling up if enabled.
    if (showOnScrollUp) {
      return scrollY > this.scrollThreshold;
    } else {
      return scrollY > this.scrollThreshold && this.scrollDirection === 'down';
    }
  }

  /**
   * Shows the sticky buy bar.
   */
  show() {
    this.classList.add('sticky-buy-bar--visible');
    this.isVisible = true;

    // Add class to all chat widget launcher instances if they exist. We can't use JS-prefixes as it's a third party injected element.
    const chatWidgetLaunchers = document.querySelectorAll('#dg-chat-widget-launcher');
    chatWidgetLaunchers.forEach((launcher) => {
      launcher.classList.add('live-chat-sticky-buy-active');
    });
  }

  /**
   * Hides the sticky buy bar.
   */
  hide() {
    this.classList.remove('sticky-buy-bar--visible');
    this.isVisible = false;

    // Remove class from all chat widget launcher instances if they exist. We can't use JS-prefixes as it's a third party injected element.
    const chatWidgetLaunchers = document.querySelectorAll('#dg-chat-widget-launcher');
    chatWidgetLaunchers.forEach((launcher) => {
      launcher.classList.remove('live-chat-sticky-buy-active');
    });
  }

  /**
   * Subscribes to variant change events using the pub/sub system.
   */
  subscribeToVariantChanges() {
    this.variantChangeSubscription = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data && event.data.variant) {
        this.updateButtonPrice(event.data.variant);
      }
    });
  }

  /**
   * Updates the button price based on variant changes.
   * @param {Object} variant - The selected variant.
   */
  updateButtonPrice(variant) {
    if (!this.mainButton) return;

    // Update variant ID for main button.
    this.mainButton.setAttribute('data-variant-id', variant.id);

    // Update button text with a small delay to ensure the main product button has updated.
    setTimeout(() => {
      const mainProductButton = document.querySelector('.js-main-product-button');
      const personalisationButton = document.querySelector('#react-personalisation-ui button');
      const mainButton = mainProductButton || personalisationButton;
      if (mainButton) {
        if (variant.available) {
          // Get personalisation and translation data.
          const hasPersonalisation = this.getAttribute('data-has-personalisation') === 'true';
          const personaliseText = this.getAttribute('data-personalise-text');
          const addToCartText = this.getAttribute('data-add-to-cart-text');

          // Construct button text based on personalisation availability.
          let buttonTextContent = addToCartText;
          if (hasPersonalisation && personaliseText) {
            buttonTextContent = personaliseText;
          }

          // Update the button text content.
          const buttonText = this.mainButton?.querySelector('.sticky-buy-bar__button-text');
          if (buttonText) {
            buttonText.textContent = buttonTextContent;
          }
        } else {
          const soldOutMessage = this.mainButton?.getAttribute('data-sold-out-message') || '';

          // Update the button text content.
          const buttonText = this.mainButton?.querySelector('.sticky-buy-bar__button-text');
          if (buttonText) {
            buttonText.textContent = soldOutMessage;
          }
        }
      }
    }, 50);
  }

  /**
   * Updates the button state based on product availability.
   */
  updateButtonState() {
    if (!this.mainButton) return;

    const mainProductButton = document.querySelector('.js-main-product-button');
    const personalisationButton = document.querySelector('#react-personalisation-ui button');
    const mainButton = mainProductButton || personalisationButton;

    if (mainButton) {
      // For personalisation button, assume it's available if it exists and is not disabled.
      const isAvailable = mainButton.getAttribute('data-available') !== 'false' && !mainButton.hasAttribute('disabled');

      if (isAvailable) {
        // Get personalisation and translation data.
        const hasPersonalisation = this.getAttribute('data-has-personalisation') === 'true';
        const personaliseText = this.getAttribute('data-personalise-text');
        const addToCartText = this.getAttribute('data-add-to-cart-text');

        // Construct button text based on personalisation availability.
        let buttonTextContent = addToCartText;
        if (hasPersonalisation && personaliseText) {
          buttonTextContent = personaliseText;
        }

        // Update the button text content.
        const buttonText = this.mainButton.querySelector('.sticky-buy-bar__button-text');
        if (buttonText) {
          buttonText.textContent = buttonTextContent;
        }
      } else {
        const soldOutMessage = this.mainButton.getAttribute('data-sold-out-message');

        // Update the button text content.
        const buttonText = this.mainButton.querySelector('.sticky-buy-bar__button-text');
        if (buttonText) {
          buttonText.textContent = soldOutMessage;
        }
      }
    }
  }

  /**
   * Handles button click events to scroll to the main product section.
   * @param {Event} event - The click event.
   */
  handleButtonClick(event) {
    event.preventDefault();

    // Find the main product section to scroll to (not just the button).
    const mainProductSection = document.querySelector('[id^="MainProduct-"]');
    if (!mainProductSection) return;

    // Scroll to the main product section with smooth behavior.
    mainProductSection.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }
}

// Register the custom element.
customElements.define('sticky-buy-bar', StickyBuyBar);
